import { inject, Injectable, InjectionToken } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { EMPTY, from, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  groupBy,
  map,
  mergeMap,
  skip,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { type WorkspaceRepository } from '@telespot/domain';
import { PaginatedStateEffects } from '@shared/ui';
import {
  AddMemberships,
  AddMembershipsError,
  RemoveMemberships,
  RemoveMembershipsError,
} from '@telespot/authentication/use-cases';
import { type IListWorkspaceMembersQuery, type IWorkspaceMembershipRepository } from '@telespot/management/use-cases';
import { UserClient } from './users.client';
import {
  NO_MEMBERSHIP,
  WorkspaceMemberData,
  WorkspaceMembersState,
  WorkspaceMembersStateModel,
} from './workspace-members.state';

export const WORKSPACE_MEMBERS_REPOSITORY = new InjectionToken<WorkspaceRepository>('WORKSPACE_MEMBERS_REPOSITORY');
export const WORKSPACE_MEMBERS_CLIENT = new InjectionToken<UserClient>('WORKSPACE_MEMBERS_CLIENT');
export const WORKSPACE_MEMBERSHIP_REPOSITORY = new InjectionToken<IWorkspaceMembershipRepository>(
  'WORKSPACE_MEMBERSHIP_REPOSITORY'
);
export const LIST_WORKSPACE_MEMBERS_QUERY = new InjectionToken<IListWorkspaceMembersQuery>(
  'LIST_WORKSPACE_MEMBERS_QUERY'
);

const MAX_CONCURRENCY = 2;

@Injectable()
export class WorkspaceMembersEffects extends PaginatedStateEffects<WorkspaceMemberData, WorkspaceMembersStateModel> {
  /**
   * Emits once each time a membership add or remove succeeds.
   * Lets the facade refresh the workspace detail counters without coupling
   * effects classes to each other.
   */
  private readonly _membershipChanged$ = new Subject<void>();
  public readonly membershipChanged$ = this._membershipChanged$.asObservable();

  private readonly workspaceRepository = inject<WorkspaceRepository>(WORKSPACE_MEMBERS_REPOSITORY);
  private readonly userClient = inject<UserClient>(WORKSPACE_MEMBERS_CLIENT);
  private readonly listWorkspaceMembersQuery = inject<IListWorkspaceMembersQuery>(LIST_WORKSPACE_MEMBERS_QUERY);
  private readonly translateService = inject(TranslateService);

  constructor(protected override readonly paginatedState: WorkspaceMembersState) {
    super(paginatedState);

    this.reinitializeOnWorkspaceChange(
      this.paginatedState.workspaceId$.pipe(
        skip(1),
        filter((workspaceId): workspaceId is string => workspaceId !== undefined)
      )
    );
  }

  // ============================================================================
  // Selectors — re-exposed from WorkspaceMembersState
  // ============================================================================

  public get workspaceId$() {
    return this.paginatedState.workspaceId$;
  }
  public get memory$() {
    return this.paginatedState.memory$;
  }
  public get memberOperationError$() {
    return this.paginatedState.memberOperationError$;
  }
  public get workspaceMembers$() {
    return this.paginatedState.workspaceMembers$;
  }
  public get membersLoading$() {
    return this.paginatedState.membersLoading$;
  }
  public get hasMoreMembers$() {
    return this.paginatedState.hasMoreMembers$;
  }
  public get membersSearchTerm$() {
    return this.paginatedState.membersSearchTerm$;
  }
  public get membersPreviousTokens$() {
    return this.paginatedState.membersPreviousTokens$;
  }
  public get membersNextToken$() {
    return this.paginatedState.membersNextToken$;
  }
  public get membersTotalCount$() {
    return this.paginatedState.membersTotalCount$;
  }
  public get membersPageSize$() {
    return this.paginatedState.membersPageSize$;
  }
  public get membersCounting$() {
    return this.paginatedState.membersCounting$;
  }

  // ============================================================================
  // Updater aliases
  // ============================================================================

  public readonly setWorkspaceId = (workspaceId: string) => this.paginatedState.setWorkspaceId(workspaceId);
  public readonly setSearchTerm = (searchTerm: string) => this.paginatedState.setSearchTerm(searchTerm);
  public readonly changePageSize = (pageSize: number) => this.paginatedState.changePageSize(pageSize);

  // Legacy alias
  public readonly loadMoreMembers = this.loadMoreItems;

  // ============================================================================
  // Custom effects
  // ============================================================================

  private readonly reinitializeOnWorkspaceChange = this.effect((trigger$: Observable<string>) =>
    trigger$.pipe(tap(() => this.initializeItems()))
  );

  /**
   * Check membership status for a list of users.
   * Each ID is processed independently using groupBy + exhaustMap.
   * If a new request arrives for an ID already being checked, it's ignored (exhausted).
   * Memory is updated as each check completes.
   */
  public readonly checkMembershipStatus = this.effect((users$: Observable<string[]>) =>
    users$.pipe(
      mergeMap((ids) => from(ids)),
      groupBy((id) => id),
      withLatestFrom(this.paginatedState.workspaceId$),
      mergeMap(([group$, workspaceId]) =>
        group$.pipe(
          exhaustMap((id) =>
            from(this.workspaceRepository.isMemberOfWorkspace(id, workspaceId ?? '')).pipe(
              map((membershipId) => ({ id, membershipId: membershipId ?? NO_MEMBERSHIP })),
              tap((result) =>
                this.paginatedState.updateUserInMemory({ userId: result.id, membershipId: result.membershipId })
              ),
              catchError(() => EMPTY)
            )
          )
        )
      )
    )
  );

  /**
   * Add a user as a member of the current workspace.
   * Uses groupBy + exhaustMap so that:
   * - Different userIds run concurrently (mergeMap across groups)
   * - Duplicate requests for the same userId while one is in progress are discarded (exhaustMap)
   * - markMembershipPending is called immediately for every incoming request
   */
  public readonly addMemberToWorkspace = this.effect((userId$: Observable<string>) =>
    userId$.pipe(
      tap((userId) => this.paginatedState.markMembershipPending(userId)),
      groupBy((userId) => userId),
      mergeMap((group$) =>
        group$.pipe(
          exhaustMap((userId) =>
            of(userId).pipe(
              concatLatestFrom(() => this.paginatedState.workspaceId$),
              filter(([, workspaceId]) => !!workspaceId),
              mergeMap(([userId, workspaceId]) =>
                from(
                  this.addUserToWorkspaces(userId, [
                    { workspaceId: workspaceId as string, membershipId: undefined, workspaceName: '' },
                  ])
                ).pipe(
                  filter((results) => results.length === 1 && results[0].membershipId !== undefined),
                  map((results) => results[0]),
                  tap((membership) => {
                    this.paginatedState.updateUserInMemory({
                      userId: userId,
                      membershipId: membership.membershipId as string,
                    });
                    this.relistAndRecount();
                    // Emit AFTER local list reset so listeners see a settled state.
                    this._membershipChanged$.next();
                  }),
                  catchError((error: Error | AddMembershipsError) => {
                    this.paginatedState.updateUserInMemory({ userId: userId, membershipId: NO_MEMBERSHIP });
                    this.handleAddMembershipError(error);
                    return EMPTY;
                  })
                )
              )
            )
          )
        )
      )
    )
  );

  /**
   * Remove a user from the current workspace.
   * Uses groupBy + exhaustMap so that:
   * - Different userIds run concurrently (mergeMap across groups)
   * - Duplicate requests for the same userId while one is in progress are discarded (exhaustMap)
   * - markMembershipPending is called immediately for every incoming request
   */
  public readonly removeMemberFromWorkspace = this.effect(
    (action$: Observable<{ userId: string; membershipId: string }>) =>
      action$.pipe(
        tap(({ userId }) => this.paginatedState.markMembershipPending(userId)),
        groupBy(({ userId }) => userId),
        mergeMap((group$) =>
          group$.pipe(
            exhaustMap(({ userId, membershipId }) =>
              of({ userId, membershipId }).pipe(
                concatLatestFrom(() => this.paginatedState.workspaceId$),
                filter(([, wsId]) => !!wsId),
                mergeMap(([{ userId, membershipId }, workspaceId]) =>
                  from(
                    this.deleteMembershipsForUser([
                      {
                        workspaceId: workspaceId as string,
                        membershipId: membershipId,
                        workspaceName: '',
                      },
                    ])
                  ).pipe(
                    filter((result) => result),
                    tap(() => {
                      this.paginatedState.updateUserInMemory({ userId: userId, membershipId: NO_MEMBERSHIP });
                      this.relistAndRecount();
                      // Emit AFTER local list reset so listeners see a settled state.
                      this._membershipChanged$.next();
                    }),
                    catchError((error: Error | RemoveMembershipsError) => {
                      this.paginatedState.updateUserInMemory({ userId: userId, membershipId: membershipId });
                      this.paginatedState.updateItemById({ id: userId, changes: { membershipId } });
                      this.handleRemoveMembershipError(error);
                      return EMPTY;
                    })
                  )
                )
              )
            )
          )
        )
      )
  );

  // ============================================================================
  // Helpers
  // ============================================================================

  private relistAndRecount() {
    this.relistPage();
    this.countItems();
  }

  private handleAddMembershipError(error: Error | AddMembershipsError) {
    console.error(error);
    this.paginatedState.setMemberOperationError(this.translateService.instant('users.error.add_membership.unknown'));
  }

  private handleRemoveMembershipError(error: Error | RemoveMembershipsError) {
    console.error(error);
    this.paginatedState.setMemberOperationError(this.translateService.instant('users.error.remove_membership.unknown'));
  }

  private async addUserToWorkspaces(
    userId: string,
    memberships: Array<{ workspaceId: string; workspaceName: string; membershipId: string | undefined }>
  ): Promise<Array<{ workspaceId: string; workspaceName: string; membershipId: string | undefined }>> {
    if (memberships.length === 0) return [];

    try {
      const newAssociations = await this.userClient.send(
        new AddMemberships({
          userId,
          workspaceIds: memberships.map((m) => m.workspaceId).filter((id) => id !== undefined),
          batchSize: MAX_CONCURRENCY,
        })
      );

      return memberships.map((membership) => {
        const association = newAssociations.find(
          (item: { workspaceId: string; membershipId: string }) => item.workspaceId === membership.workspaceId
        );
        return { ...membership, membershipId: association?.membershipId };
      });
    } catch (error) {
      this.handleAddMembershipError(error as Error | AddMembershipsError);
      return [];
    }
  }

  private async deleteMembershipsForUser(
    memberships: Array<{ workspaceId: string; workspaceName: string; membershipId: string | undefined }>
  ): Promise<boolean> {
    if (memberships.length === 0) return true;

    try {
      await this.userClient.send(
        new RemoveMemberships({
          membershipIds: memberships.map((m) => m.membershipId).filter((id): id is string => id !== undefined),
          batchSize: MAX_CONCURRENCY,
        })
      );
      return true;
    } catch (error) {
      this.handleRemoveMembershipError(error as Error | RemoveMembershipsError);
      return false;
    }
  }

  // ============================================================================
  // Abstract method implementations
  // ============================================================================

  protected override async countRepository(searchTerm?: string): Promise<number> {
    const workspaceId = await this.paginatedState.workspaceId$.pipe(take(1)).toPromise();
    if (!workspaceId) return 0;
    return this.workspaceRepository.countMembers(workspaceId, searchTerm);
  }

  protected override listFlow(
    limit: number,
    lastItem: WorkspaceMemberData | undefined,
    searchTerm: string | undefined
  ): Observable<WorkspaceMemberData[]> {
    return this.paginatedState.workspaceId$.pipe(
      take(1),
      switchMap((workspaceId) => {
        if (!workspaceId) return of([] as WorkspaceMemberData[]);

        return from(
          this.listWorkspaceMembersQuery.execute({
            workspaceId,
            limit,
            searchTerm,
            lastMembershipId: lastItem?.membershipId,
          })
        ).pipe(
          map((members) => {
            const membershipMap = members.reduce(
              (acc, m) => ({ ...acc, [m.userId]: m.membershipId }),
              {} as Record<string, string>
            );
            this.paginatedState.extendMemory(membershipMap);
            return members.map<WorkspaceMemberData>((m) => ({
              id: m.userId,
              username: m.username,
              avatarUrl: undefined,
              emailVerified: m.userVerified,
              membershipId: m.membershipId,
            }));
          })
        );
      })
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected override async deleteRepositoryItem(_itemId: string): Promise<boolean> {
    return false;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected override processDeferredAttributes(_itemId: string): Observable<Partial<WorkspaceMemberData>> {
    return of();
  }
}
